import { Component, EventEmitter, Input, Output } from '@angular/core';
import { UntypedFormArray, UntypedFormGroup } from '@angular/forms';
import {
  SupportsTable,
  SupportsTableColumn,
  SupportsTableRemoval,
} from './supports-table.model';

const PERCENT_TOLERANCE = 0.001;

@Component({
  selector: 'supports-table',
  templateUrl: './supports-table.component.html',
  styleUrls: ['./supports-table.component.scss'],
  standalone: false,
})
export class SupportsTableComponent {
  @Input() supports: UntypedFormArray;
  @Input() config: SupportsTable;

  @Output() addSupport: EventEmitter<void> = new EventEmitter();
  @Output() removeSupport: EventEmitter<SupportsTableRemoval> =
    new EventEmitter();

  get rows(): UntypedFormGroup[] {
    const rows = (this.supports?.controls || []) as UntypedFormGroup[];
    return this.config?.rowFilter ? rows.filter(this.config.rowFilter) : rows;
  }

  get columns(): SupportsTableColumn[] {
    return this.config?.columns || [];
  }

  get removable(): boolean {
    return (
      !!this.config?.removable && this.rows.length > (this.config.minRows || 0)
    );
  }

  get hasTotal(): boolean {
    return this.rows.length > 0 && this.columns.some(column => column.total);
  }

  get percentColumn(): SupportsTableColumn {
    return this.columns.find(column => column.total && column.unit === '%');
  }

  get hasRemainingAmount(): boolean {
    return (
      this.config?.remainingAmount !== null &&
      this.config?.remainingAmount !== undefined
    );
  }

  get remainingPercent(): number {
    const target = this.config?.targetPercent;
    const column = this.percentColumn;
    if (target == null || !column) return null;
    return Number((target - this.columnTotal(column)).toFixed(4));
  }

  get isComplete(): boolean {
    const remaining = this.remainingPercent;
    return remaining !== null && Math.abs(remaining) < PERCENT_TOLERANCE;
  }

  columnKey(column: SupportsTableColumn): string {
    return column.controlName || column.valueName;
  }

  columnValue(row: UntypedFormGroup, column: SupportsTableColumn) {
    return row.get(this.columnKey(column))?.value;
  }

  columnTotal(column: SupportsTableColumn): number {
    return this.rows.reduce(
      (total, row) => total + (Number(this.columnValue(row, column)) || 0),
      0,
    );
  }

  columnMax(row: UntypedFormGroup, column: SupportsTableColumn): number {
    return typeof column.max === 'function' ? column.max(row) : column.max;
  }

  isEmptyWarning(row: UntypedFormGroup, column: SupportsTableColumn): boolean {
    if (!column.warnWhenEmpty) return false;
    const value = this.columnValue(row, column);
    return value === null || value === undefined || value === '';
  }

  supportName(row: UntypedFormGroup, column: SupportsTableColumn) {
    return row.get(column.nameControlName || 'name')?.value;
  }

  supportIsin(row: UntypedFormGroup, column: SupportsTableColumn) {
    return row.get(column.isinControlName || 'isin')?.value;
  }

  supportDetail(row: UntypedFormGroup, column: SupportsTableColumn): string {
    const isin = this.supportIsin(row, column);
    const typeId = row.get(column.typeControlName || 'type')?.value;
    const typeLabel = typeId
      ? (this.config?.getTypeLabel?.(typeId) ?? typeId)
      : '';
    return [isin, typeLabel].filter(Boolean).join(' · ');
  }

  removeLabel(row: UntypedFormGroup): string {
    const column = this.columns.find(item => item.type === 'support');
    if (!column) return '';
    const name = this.supportName(row, column);
    const isin = this.supportIsin(row, column);
    if (name && isin) return `${name} (${isin})`;
    return name || isin || '';
  }

  remove(row: UntypedFormGroup, index: number) {
    this.removeSupport.emit({ index, value: row.value });
  }
}
